<script setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNotificationsStore } from '@/stores/notifications'
import AppIcon from '@/components/AppIcon.vue'
import AppTooltip from '@/components/AppTooltip.vue'

const props = defineProps({
  sidebar: { type: Boolean, default: false },
})

const router = useRouter()
const route = useRoute()
const store = useNotificationsStore()
const open = ref(false)

const fmt = iso => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

const onClick = async n => {
  await store.markRead(n.id)
  open.value = false
  if (n.ticket_id) {
    router.push({
      name: 'ticket',
      params: { id: n.ticket_id },
      query: { returnTo: route.fullPath },
    })
  }
}

onMounted(() => store.fetch())
</script>

<template>
  <div class="relative">
    <AppTooltip text="Notificações" :placement="sidebar ? 'right' : 'bottom'">
      <button
        type="button"
        aria-label="Notificações"
        class="relative grid place-items-center transition-colors"
        :class="sidebar
          ? 'size-10 rounded-xl text-white/45 hover:bg-white/10 hover:text-white'
          : 'size-9 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800'"
        @click="open = !open"
      >
        <AppIcon name="bell" :size="18" />
        <span
          v-if="store.unreadCount"
          class="absolute grid min-w-4 place-items-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold leading-4 text-white"
          :class="sidebar ? '-right-1 top-0' : '-right-0.5 -top-0.5'"
        >
          {{ store.unreadCount > 9 ? '9+' : store.unreadCount }}
        </span>
      </button>
    </AppTooltip>

    <!-- Backdrop pra fechar ao clicar fora -->
    <div v-if="open" class="fixed inset-0 z-10" @click="open = false" />

    <div
      v-if="open"
      class="absolute z-20 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
      :class="sidebar ? 'bottom-0 left-full ml-3' : 'right-0 mt-2'"
    >
      <div class="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <span class="text-sm font-semibold text-slate-800">Notificações</span>
        <button
          v-if="store.unreadCount"
          class="text-xs font-semibold text-[#6f9b36] hover:underline"
          @click="store.markAllRead()"
        >
          Marcar todas como lidas
        </button>
      </div>

      <div class="max-h-96 overflow-y-auto">
        <p v-if="!store.list.length" class="px-4 py-8 text-center text-sm text-slate-400">
          Nenhuma notificação.
        </p>
        <button
          v-for="n in store.list"
          :key="n.id"
          class="flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left hover:bg-slate-50"
          @click="onClick(n)"
        >
          <span
            class="mt-1.5 size-2 shrink-0 rounded-full"
            :class="n.read ? 'bg-transparent' : 'bg-[#98c759]'"
          />
          <span class="min-w-0">
            <span class="block text-sm text-slate-800" :class="{ 'font-medium': !n.read }">{{ n.title }}</span>
            <span class="block text-xs text-slate-400">{{ fmt(n.created_at) }}</span>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
